import { FC } from 'react';
import { CircularProgress } from '@mui/material';

import { GroupedInvoiceFileProcessingStatusDto, InvoiceFileProcessingStatusDto } from 'src/generated/services/TFinancialApi';
import { useNotificationData } from 'src/use-cases/notification/useNotificationData';
import { useNotificationUI } from 'src/use-cases/notification/useNotificationUI';

import { UploadStatusItem } from '../UploadStatusItem';
import { useNotificationSelector } from '../NotificationContext';
import { NotificationDetailSectionProvider } from './SectionProviders';
import { INotifDetailInvoiceFilesProps } from './types';
import { NotificationDetailSectionUnderReview } from './SectionUnderReview';
import { NotificationDetailSectionLoader } from './SectionLoader';
import { NotificationDetailSectionOther } from './SectionOther';

export const NotificationDetailInvoiceFiles: FC<INotifDetailInvoiceFilesProps> = ({
  errorInvoiceFileProcessingStatuses,
  defaultExpanded: propDefaultExpanded,
}) => {
  const { selectedDetail } = useNotificationSelector();

  const { defaultExpanded, errorProcessingStatuses } = useNotificationUI();

  // ✅ FIX: Only fetch data when batchId exists
  const batchId = selectedDetail?.batchId || null;
  const { providerLists, otherLists, underReviewLists, processingLists, isLoading } = useNotificationData(
    batchId,
    errorInvoiceFileProcessingStatuses || errorProcessingStatuses
  );

  const finalDefaultExpanded = propDefaultExpanded !== undefined ? propDefaultExpanded : defaultExpanded;

  // ✅ FIX: Show message when no batch selected
  if (!batchId) {
    return (
      <div className='p-24 bg-neutral-10 flex-1 overflow-y-auto flex items-center justify-center'>
        <p className='text-neutral-50'>No notification selected</p>
      </div>
    );
  }

  return (
    <div className='p-24 bg-neutral-10 flex-1 overflow-y-auto'>
      {isLoading ? (
        <CircularProgress size={24} color='primary' />
      ) : (
        <div className='flex flex-col gap-10'>
          {providerLists?.map((groupedDetail: GroupedInvoiceFileProcessingStatusDto) => (
            <div key={`provider-${groupedDetail.serviceProviderCode || 'unknown'}`}>
              {groupedDetail?.serviceProviderCode && (
                <NotificationDetailSectionProvider data={groupedDetail} defaultExpanded={finalDefaultExpanded}>
                  {groupedDetail.invoiceGroups?.map((invoiceGroup) => (
                    <UploadStatusItem
                      key={invoiceGroup.invoiceFileProcessingStatusList?.[0]?.invoiceFileProcessingId || 'unknown'}
                      dataList={invoiceGroup.invoiceFileProcessingStatusList || []}
                      errorInvoiceFileProcessingStatuses={errorInvoiceFileProcessingStatuses || errorProcessingStatuses}
                    />
                  ))}
                </NotificationDetailSectionProvider>
              )}
            </div>
          ))}

          {/* Other Section - Priority 2: Files with resolution issues + under review */}
          {/* Each file should be a separate card to show its unique fileResolutionStatus chip */}
          {otherLists?.map((groupedDetail: GroupedInvoiceFileProcessingStatusDto) => {
            // Flatten all files from all invoice groups into individual cards
            const allFiles = groupedDetail.invoiceGroups?.flatMap((group) => group.invoiceFileProcessingStatusList || []) || [];

            return (
              <NotificationDetailSectionOther
                key={`other-${groupedDetail.serviceProviderCode || 'null'}-${allFiles[0]?.invoiceFileProcessingId || 'unknown'}`}
                data={groupedDetail}
                defaultExpanded={finalDefaultExpanded}
              >
                {allFiles.map((file) => (
                  <UploadStatusItem
                    key={file.invoiceFileProcessingId || 'unknown'}
                    data={file}
                    errorInvoiceFileProcessingStatuses={errorInvoiceFileProcessingStatuses || errorProcessingStatuses}
                  />
                ))}
              </NotificationDetailSectionOther>
            );
          })}

          {/* Under Review Section - Deprecated (now included in Other section) */}
          {/* Kept for backward compatibility but should be empty */}
          {/* Each file as separate card to show unique status chips */}
          {underReviewLists?.map((groupedDetail: GroupedInvoiceFileProcessingStatusDto) => {
            const allFiles = groupedDetail.invoiceGroups?.flatMap((group) => group.invoiceFileProcessingStatusList || []) || [];

            return (
              <NotificationDetailSectionUnderReview
                key={`underreview-${groupedDetail.serviceProviderCode || 'null'}-${allFiles[0]?.invoiceFileProcessingId || 'unknown'}`}
                data={groupedDetail}
                defaultExpanded={finalDefaultExpanded}
              >
                {allFiles.map((file) => (
                  <UploadStatusItem
                    key={file.invoiceFileProcessingId || 'unknown'}
                    data={file}
                    errorInvoiceFileProcessingStatuses={errorInvoiceFileProcessingStatuses || errorProcessingStatuses}
                  />
                ))}
              </NotificationDetailSectionUnderReview>
            );
          })}

          {processingLists?.map((groupedDetail: GroupedInvoiceFileProcessingStatusDto) => (
            <NotificationDetailSectionLoader
              key={`processing-${groupedDetail.serviceProviderCode || 'null'}-${
                groupedDetail.invoiceGroups?.[0]?.invoiceFileProcessingStatusList?.[0]?.invoiceFileProcessingId || 'unknown'
              }`}
              data={groupedDetail}
              defaultExpanded={finalDefaultExpanded}
            >
              {groupedDetail.invoiceGroups?.map((invoiceGroup) => (
                <UploadStatusItem
                  key={invoiceGroup.invoiceFileProcessingStatusList?.[0]?.invoiceFileProcessingId || 'unknown'}
                  dataList={invoiceGroup.invoiceFileProcessingStatusList || []}
                  errorInvoiceFileProcessingStatuses={errorInvoiceFileProcessingStatuses || errorProcessingStatuses}
                />
              ))}
            </NotificationDetailSectionLoader>
          ))}
        </div>
      )}
    </div>
  );
};
